import type { Building } from "building-types/building/buildingTypes";
import type { ComponentInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ComponentTransform } from "./componentTypes";

/**
 * Returns source room if it exists, otherwise returns the room ID.
 */
export function getSourceRoomId(room: Room): string {
  return room.sourceRoomId ?? room.id;
}

/**
 * Represents an entity that may be hosted by a room, allowing extraction
 * of component instance information when the room belongs to a component instance.
 */
export type RoomHostedPartialEntity =
  | {
      type: "WINDOW" | "WINDOW_V2" | "DOOR" | "DOOR_V2";
      frontRoom?: Room;
      backRoom?: Room;
    }
  | { entityType: "PROP" | "CASEWORK"; room: Room | null }
  | { entityType: "COLUMN"; roomId: string }
  | { entityType: "ANNOTATION"; parentId?: string }
  | { entityType: "CEILING"; section: { roomId: string } };

/**
 * Given a building entity that may be hosted by a room, returns the ComponentInstanceHandle
 * if the hosting room belongs to a component instance.
 */
export function getComponentInstanceHandleFromRoomHostedEntity(
  building: Building,
  entity: RoomHostedPartialEntity,
): ComponentInstanceHandle | undefined {
  if (!building.enabledFeatures?.modelComponents) {
    return undefined;
  }

  let room: Room | null | undefined;
  if ("entityType" in entity) {
    switch (entity.entityType) {
      case "PROP":
      case "CASEWORK":
        room = entity.room;
        break;
      case "COLUMN":
        // Column: look up room by roomId
        room = building.rooms[entity.roomId];
        break;
      case "ANNOTATION":
        // Annotation: look up room by parentId if it's a room
        if (entity.parentId) {
          room = building.rooms[entity.parentId];
        }
        break;
      case "CEILING": {
        // Ceiling: look up room by section's roomId
        const roomId = entity.section?.roomId;
        if (roomId) {
          room = building.rooms[roomId];
        }
        break;
      }
    }
  } else {
    // Window/Door: check frontRoom first, then backRoom
    if (entity.frontRoom?.sourceRoomId && entity.frontRoom?.instanceId) {
      room = entity.frontRoom;
    } else if (entity.backRoom?.sourceRoomId && entity.backRoom?.instanceId) {
      room = entity.backRoom;
    }
  }

  if (room?.sourceRoomId && room?.instanceId) {
    return { type: "COMPONENT_INSTANCE", id: room.instanceId };
  }

  return undefined;
}

export function isCombinedGlobalInstanceMirrorFromTransform(
  globalMirror: boolean,
  componentTransform: ComponentTransform | undefined,
): boolean {
  return globalMirror !== (componentTransform?.reverseOrientation ?? false);
}
